
// Base Entity for Audit & Sync
export interface BaseEntity {
  created_at?: string;
  updated_at?: string;
  deleted_at?: string; // Soft Delete
  server_version: number; // Monotonic authoritative version from server
}

// Auth & RBAC
export type Role = 'Admin' | 'Guest' | 'VIP' | 'Hunter' | 'Manager' | 'Family' | 'Friends' | 'Worker';

export interface Privilege {
  view: boolean;
  write: boolean;
}

export type MenuPageKey =
  | 'map'
  | 'places'
  | 'overview'
  | 'scouting'
  | 'projects'
  | 'assets'
  | 'documents'
  | 'settings';

export interface MenuPage {
  key: MenuPageKey;
  label: string;
  path: string;
  category: string;
  description?: string;
}

export type FeaturePrivilegeKey = MenuPageKey | 'gpsTracking' | 'contributionReview' | 'communityParticipation' | 'photoModeration' | 'neighborContacts';
export type PrivilegeMap = Record<FeaturePrivilegeKey, Privilege>;

export interface RoleDefinition extends BaseEntity {
  role: Role;
  privileges: PrivilegeMap;
}

export type UserStatus = 'Active' | 'Invited' | 'Pending' | 'Suspended';

export interface UserAccount extends BaseEntity {
  email: string;
  name: string;
  picture?: string;
  role: Role;
  status: UserStatus;
  roles?: Role[];
  farm_ids?: string[];
  bio?: string;
  profile_saved_at?: string;
  time_format?: '12h' | '24h';
  time_format_revision?: number;
  arrival_detection?: boolean;
  visited_before?: boolean;
  onboarding_completed_at?: string;
  onboarding_eligible?: boolean;
  onboarding_farm_id?: string | null;
  visited_before_answered?: boolean;
  privileges?: PrivilegeMap;
  invitedAt?: string;
}

export interface GoogleProfile {
  email: string;
  name?: string;
  picture?: string;
  sub?: string;
}

// --- Sync & Architecture Types ---

export type MutationType = 'create' | 'update' | 'delete';

export interface MutationOperation {
  op_id: string;             // Idempotency Key (UUID)
  mutation_id: string;       // Stable id for deduplication
  device_id?: string;        // Optional device identifier
  table_name: string;        // e.g., 'farms', 'contracts'
  type: MutationType;
  entity_id: string;
  base_version: number;      // The version the client had when it started the edit (for OCC)
  payload: any;              // The data (minimal or full)
  created_at_local: string;  // Client-side timestamp
  status: 'pending' | 'processing' | 'failed' | 'conflict';
  retry_count: number;
  error?: string;
  server_entity?: any;
  owner_account?: string;
}

export interface SyncState {
  key: string;               // singleton key, e.g., 'global_sync'
  last_pulled_at: string | null;
  last_sync_epoch: number;   // To detect server-side resets
  cursor?: string;
  scope?: string;
  stream_id?: string;
}

// Enums (unchanged for brevity, but logically part of the file)
export enum ContractStatus { Draft = 'Draft', Active = 'Active', Expired = 'Expired', Terminated = 'Terminated' }
export enum ContractType { CRP = 'CRP', Lease = 'Lease' }
export enum CorrespondenceDirection { Inbound = 'inbound', Outbound = 'outbound' }
export enum CorrespondenceChannel { Email = 'email', PostalLetter = 'postal_letter', PhoneLog = 'phone_log', InternalNote = 'internal_note' }
export enum DocumentSource { Uploaded = 'uploaded', EmailAttachment = 'email_attachment', Scanned = 'scanned', Downloaded = 'downloaded' }
export enum GeometryType { Point = 'point', Polyline = 'polyline', Polygon = 'polygon' }
export enum MapFeatureType { Field = 'field', Tract = 'tract', Farm = 'farm', PointOfInterest = 'poi', Trail = 'trail', Structure = 'structure', Other = 'other' }
export enum AssetCondition { New = 'New', Used = 'Used', Refurbished = 'Refurbished' }
export enum AssetStatus { Active = 'Active', Maintenance = 'Maintenance', Sold = 'Sold', Retired = 'Retired' }
export enum ContactType { Individual = 'Individual', Business = 'Business', Government = 'Government' }

export interface RawGpxFeature {
  id: string;
  name: string;
  type: 'Track' | 'Route' | 'Waypoint';
  coordinates: [number, number][] | [number, number];
}

// 1. Categories and Conservation Practices
export interface CPCategory extends BaseEntity {
  category_id: string;
  name: string;
  description?: string;
  display_order?: number;
}

export interface ConservationPractice extends BaseEntity {
  cp_id: string; 
  title: string;
  description?: string;
  category_id: string; 
  program_type?: string;
  status?: string;
  primary_purpose?: string;
  min_contract_years?: number;
  max_contract_years?: number;
  typical_contract_years?: number;
  mid_contract_mgmt_required: boolean;
  mid_contract_mgmt_year_from?: number;
  mid_contract_mgmt_year_to?: number;
  allowed_activities_notes?: string;
  restrictions_notes?: string;
  usda_reference?: string;
  last_reviewed?: string;
}

// 2. Farms, Tracts, and Fields
export interface Farm extends BaseEntity {
  farm_id: string;
  name: string;
  address?: string;
  llc_name?: string;
  usda_farm_number?: string;
  county?: string;
  state?: string;
  notes?: string;
  primary_gpx_document_id?: string;
  boundary_geojson?: string;
  poi_colors?: Record<string,string>;
}

export interface FarmTract extends BaseEntity {
  tract_id: string;
  farm_id: string;
  usda_tract_number: string;
  description?: string;
  boundary_geojson?: string;
}

export interface FarmField extends BaseEntity {
  field_id: string;
  tract_id: string;
  usda_field_number: string;
  subfield_code?: string;
  gis_reference?: string;
  total_acres?: number;
  boundary_geojson?: string;
  color?: string; 
}

export interface Contact extends BaseEntity {
  contact_id: string;
  name: string;
  type: ContactType;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  notes?: string;
}

// 3. CRP Contracts and Enrollment
export interface CRPContract extends BaseEntity {
  contract_id: string;
  contract_no: string;
  type: ContractType;
  farm_id: string;
  participant_name: string;
  participant_contact_id?: string;
  start_date: string;
  end_date: string;
  status: ContractStatus;
  total_enrolled_acres?: number;
  avg_rental_rate_per_acre?: number;
  fsa_office?: string;
  notes?: string;
}

export interface ContractPractice extends BaseEntity {
  contract_practice_id: string;
  contract_id: string;
  cp_id: string;
  total_acres: number;
  rental_rate_per_acre?: number;
  signing_incentive_payment?: number;
  practice_incentive_payment?: number;
  other_payments_notes?: string;
  active: boolean;
}

export interface ContractField extends BaseEntity {
  contract_field_id: string;
  contract_practice_id: string;
  field_id: string;
  enrolled_acres: number;
  active: boolean;
  restrictions_override?: string;
}

// 4. Assets (Equipment)
export interface Asset extends BaseEntity {
  asset_id: string;
  name: string;
  brand: string;
  model_year?: number;
  serial_number?: string;
  purchase_date?: string;
  purchase_price?: number;
  condition_at_purchase?: AssetCondition;
  warranty_expiration?: string;
  description?: string;
  status: AssetStatus;
  notes?: string;
}

export type ProjectStatus = 'todo' | 'in_process' | 'paused' | 'completed' | 'archived';

export interface Project extends BaseEntity {
  priority_group?: 'now'|'next'|'later';
  next_step?: string;
  materials?: string;
  target_date?: string|null;
  revision?: number;
  series_id?: string|null;
  skipped_reason?: string|null;
  farm_id?:string|null;
  id: string;
  name: string;
  description?: string;
  status: ProjectStatus;
  priority_rank?: number;
  created_at: string;
  updated_at: string;
}

export type WorkLogState = 'active' | 'paused' | 'stopped';

export interface WorkLog extends BaseEntity {
  farm_id?: string;
  project_name?: string;
  entry_kind?: 'timer'|'manual';
  worked_on?: string;
  manual_minutes?: number;
  notes?: string;
  contributor_name?: string;
  id: string;
  project_id: string;
  user_id: string;
  start_time: string|null;
  end_time?: string|null;
  state: WorkLogState;
  duration_ms?: number;
  revision?: number;
  contributor_email?: string;
  intervals?: {start:string;end:string|null}[];
  review_status?: string;
  review_reason?: string;
  sync_pending?: boolean;
}

export const deriveWorkLogDurationMs = (workLog: WorkLog, nowIso: string = new Date().toISOString()): number => {
  if(workLog.entry_kind==='manual')return (workLog.manual_minutes||0)*60000;
  if (workLog.intervals?.length) return workLog.intervals.reduce((total, i) => {
    const end = i.end || (workLog.state === 'active' ? nowIso : i.start);
    const elapsed = Date.parse(end) - Date.parse(i.start);
    return total + (Number.isFinite(elapsed) ? Math.max(0, elapsed) : 0);
  }, 0);
  const startMs = Date.parse(workLog.start_time||'');
  if (Number.isNaN(startMs)) return 0;

  const effectiveEnd = workLog.end_time || (workLog.state === 'active' ? nowIso : workLog.start_time||'');
  const endMs = Date.parse(effectiveEnd);
  if (Number.isNaN(endMs)) return 0;

  return Math.max(0, endMs - startMs);
};

// 5. Operations and Compliance
export interface OMTask extends BaseEntity {
  task_id: string;
  related_entity_type?: 'farm' | 'farm_field' | 'contract_field' | 'asset' | 'infrastructure' | 'farm_tract' | 'map_feature';
  related_entity_id?: string;
  contract_field_id?: string; 
  asset_id?: string; 
  task_type: string;
  estimated_cost?: number;
  window_start?: string;
  window_end?: string;
  scheduled_date?: string;
  completed_date?: string;
  performed_by?: string;
  notes?: string;
}

export interface ComplianceEvent extends BaseEntity {
  event_id: string;
  contract_field_id: string;
  event_date: string;
  event_type: string;
  description?: string;
  resolved: boolean;
  resolved_date?: string;
  resolution_notes?: string;
}

// 6. Finance
export interface Transaction extends BaseEntity {
  transaction_id: string;
  date: string;
  amount: number;
  type: 'income' | 'expense';
  category: string;
  payee?: string;
  related_contact_id?: string;
  description?: string;
  related_task_id?: string;
  contract_id?: string;
  asset_id?: string;
  farm_id?: string;
}

// 7. Documents and Correspondence
export interface CorrespondenceMessage extends BaseEntity {
  message_id: string;
  direction: CorrespondenceDirection;
  sender?: string;
  recipient?: string;
  subject?: string;
  body_text?: string;
  body_html?: string;
  message_datetime: string;
  channel: CorrespondenceChannel;
  usda_office?: string;
  thread_id?: string;
  has_attachment: boolean;
  notes?: string;
}

export interface Document extends BaseEntity {
  document_id: string;
  farm_id?:string|null;
  primary_photo_id?:string|null;
  title: string;
  description?: string;
  file_path: string;
  file_base64?: string;
  file_type?: string;
  source?: DocumentSource;
  created_date?: string;
  uploaded_date: string;
  is_confidential: boolean;
  version?: string;
  notes?: string;
  // Metadata for Cloud Storage
  gcs_path?: string;
  presigned_url?: string;
  presigned_expiry?: string;
}

export interface RecordLink extends BaseEntity {
  record_link_id: string;
  from_record_type: 'farm' | 'farm_tract' | 'farm_field' | 'contract' | 'contract_practice' | 'contract_field' | 'conservation_practice' | 'task' | 'asset' | 'transaction' | 'contact' | 'document' | 'scouting_photo' | 'map_feature' | 'project' | 'message';
  from_record_id: string;
  to_record_type: 'farm' | 'farm_tract' | 'farm_field' | 'contract' | 'contract_practice' | 'contract_field' | 'conservation_practice' | 'task' | 'asset' | 'transaction' | 'contact' | 'document' | 'scouting_photo' | 'map_feature' | 'project' | 'message';
  to_record_id: string;
  // Legacy compatibility fields (deprecated)
  entity_type?: 'farm' | 'farm_tract' | 'farm_field' | 'contract' | 'contract_practice' | 'contract_field' | 'conservation_practice' | 'task' | 'asset' | 'transaction' | 'contact' | 'document' | 'scouting_photo' | 'map_feature' | 'project' | 'message';
  entity_id?: string;
  document_id?: string;
  message_id?: string;
  photo_id?: string;
  linked_by?: string;
  linked_date: string;
}

// 8. Mapping and GPX
export interface MapFeature extends BaseEntity {
  map_feature_id: string;
  farm_id: string;
  feature_type?: MapFeatureType;
  is_inactive?: boolean;
  point_kind?: 'blind' | 'tree_stand' | 'barn' | 'outbuilding' | 'cabin' | 'gate' | 'camera' | 'parking' | 'other' | null;
  entity_type?: 'farm' | 'farm_tract' | 'farm_field' | 'contract_field';
  entity_id?: string;
  geometry_type: GeometryType;
  coordinates_geojson: string;
  label?: string;
  color?: string;
  fill_color?: string;
  stroke_weight?: number;
  fill_opacity?: number;
  source: 'manual_draw' | 'imported_gpx';
  gpx_source_document_id?: string;
  gpx_source_segment_name?: string;
  notes?: string;
}


// GPS Tracking
export interface GpsTrackPoint {
    routeId?: string;
    farmId?: string;
    ownerEmail?: string;
  id: string;
  sessionId: string;
  ts: number;
  lat: number;
  lng: number;
  accuracyM?: number;
  headingDeg?: number;
  speedMps?: number;
  provider: 'browser';
  source: 'watchPosition' | 'manual';
  createdAt: string;
}

export type GeoPermissionState = PermissionState | 'unsupported';
export type GpsTrackingStatus = 'idle' | 'starting' | 'tracking' | 'paused' | 'stopped' | 'error';

export interface LiveGpsCoordinate {
  lat: number;
  lng: number;
  accuracyM?: number;
  headingDeg?: number;
  speedMps?: number;
  ts: number;
}

export interface LiveUserLocation {
  isLive?: boolean;
  sessionId: string;
  userSub: string;
  userEmail: string;
  userName?: string;
  userPicture?: string | null;
  coords: LiveGpsCoordinate;
  lastUpdated: number;
}

export interface GpsTrackingUiState {
  permissionState: GeoPermissionState;
  trackingState: GpsTrackingStatus;
  lastFix: GeolocationPosition | null;
  inGeofence: boolean | null;
  followMe: boolean;
  errors: string[];
}

// 9. Scouting Photos
export enum ScoutingPhotoSourceType {
  TrailCam = 'Trail-Cam',
  User = 'User',
  Upload = 'Upload',
}

export type ScoutingPhotoSource = `${ScoutingPhotoSourceType}`;

export interface ScoutingPhotoLocation {
  lat?: number;
  lng?: number;
  alt?: number;
}

export interface ScoutingPhotoExifMetadata {
  taken_at?: string;
  location?: ScoutingPhotoLocation;
}

export interface ScoutingPhotoUploadRequest {
  farm_id?: string;
  /** Permanent Places import tag, e.g. place:<map_feature_id>. */
  place_tag?: string;
  photo_id?: string;
  source: ScoutingPhotoSource;
  created_by?: string;
  uploaded_at?: string;
  taken_at?: string;
  notes?: string;
  tag_category_ids?: string[];
  file_base64: string;
  file_name: string;
  exif?: ScoutingPhotoExifMetadata;
}

export interface ScoutingPhotoCategory extends BaseEntity {
    admin_only?: boolean;
  category_id: string;
  name: string;
  color?: string;
  description?: string;
}

export interface ScoutingPhoto extends BaseEntity {
  photo_id: string;
  source: ScoutingPhotoSource;
  created_by: string;
  exif_lat?: number;
  exif_lng?: number;
  exif_alt?: number;
  taken_at?: string;
  uploaded_at: string;
  storage_url: string;
  image_url?: string;
  thumbnail_url?: string;
  display_url_expires_at?: string | null;
  display_url_ttl_seconds?: number | null;
  notes?: string;
  tag_category_ids?: string[];
  comment_count?: number;
}

export interface ScoutingPhotoCategoryLink extends BaseEntity {
  photo_id: string;
  category_id: string;
}
